// Settings modal — configure API keys. Syncs to/from Cloudflare KV.
// Keys are masked after entry (placeholder shows last 4 chars).
// Leaving a field blank on save keeps the existing stored value.

import { workerFetch, AUTH_TOKEN_KEY, QUIVER_KEY, ETHERSCAN_KEY, WHALE_ALERT_KEY } from './worker';
import { alpacaFetch } from './alpaca';
import { fetchAndApplySettings } from './auth';
import { esc } from './utils';

interface ServiceStatus { ok: boolean; msg: string; }

const FIELDS = {
  alpacaKey:    'meridian_alpaca_key',
  alpacaSecret: 'meridian_alpaca_secret',
  scriptUrl:    'meridian_script_url',
  scriptToken:  'meridian_token',
  authToken:    AUTH_TOKEN_KEY,
  quiverKey:    QUIVER_KEY,
  etherscanKey: ETHERSCAN_KEY,
  whaleAlertKey: WHALE_ALERT_KEY,
} as const;

// Keys excluded from KV sync (auth token gates the fetch, script URL is not secret)
const KV_EXCLUDE = new Set([FIELDS.authToken, FIELDS.scriptUrl]);

function get(key: string): string {
  return localStorage.getItem(key) ?? '';
}
function set(key: string, val: string): void {
  if (val) localStorage.setItem(key, val);
  else localStorage.removeItem(key);
}

function masked(key: string): string {
  const val = get(key);
  return val ? '••••••••' + val.slice(-4) : '';
}

// ── KV sync ───────────────────────────────────────────────────────────────────

export async function saveToKV(): Promise<void> {
  const token = get(FIELDS.authToken);
  if (!token) return;
  const data: Record<string, string> = {};
  for (const key of Object.values(FIELDS)) {
    if (KV_EXCLUDE.has(key)) continue;
    const val = get(key);
    if (val) data[key] = val;
  }
  try {
    await workerFetch('/api/settings', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  } catch { /* silent */ }
}

// ── Connection testers ────────────────────────────────────────────────────────

async function testAlpaca(): Promise<ServiceStatus> {
  if (!get(FIELDS.alpacaKey) || !get(FIELDS.alpacaSecret))
    return { ok: false, msg: 'Keys not configured' };
  try {
    const d = await alpacaFetch<any>('/v2/account');
    return { ok: true, msg: `Paper account · $${parseFloat(d.equity).toLocaleString('en-US', { maximumFractionDigits: 0 })} equity` };
  } catch (e: unknown) {
    return { ok: false, msg: e instanceof Error ? e.message : 'Request failed' };
  }
}

async function testAuth(): Promise<ServiceStatus> {
  const token = get(FIELDS.authToken);
  if (!token) return { ok: false, msg: 'No key configured' };
  try {
    await workerFetch('/api/auth-check', { method: 'POST', body: '{}', headers: { 'Content-Type': 'application/json' } });
    return { ok: true, msg: 'Key accepted' };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, msg: msg.includes('401') ? 'Wrong key' : msg };
  }
}

async function testQuiver(): Promise<ServiceStatus> {
  const key = get(FIELDS.quiverKey);
  try {
    const headers: Record<string, string> = {};
    if (key) headers['X-Quiver-Key'] = key;
    const d = await workerFetch<{ count?: number; error?: string }>('/api/congress-trades', { headers });
    if (d.error) return { ok: false, msg: d.error };
    const note = key ? '' : ' (public feed — add key for premium data)';
    return { ok: true, msg: `Connected — ${d.count?.toLocaleString() ?? '?'} trades available${note}` };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, msg: msg.includes('401') || msg.includes('403') ? 'Invalid API key' : msg };
  }
}

async function testSheets(): Promise<ServiceStatus> {
  if (!get(FIELDS.scriptUrl) || !get(FIELDS.scriptToken))
    return { ok: false, msg: 'Not configured (optional)' };
  try {
    const d = await workerFetch<any>(`/api/sheet?tab=Tickers`);
    const rows = (d.rows ?? []).length;
    return { ok: true, msg: `Connected · ${rows} ticker${rows !== 1 ? 's' : ''} in sheet` };
  } catch (e: unknown) {
    return { ok: false, msg: e instanceof Error ? e.message : 'Request failed' };
  }
}

// ── Status badge helpers ──────────────────────────────────────────────────────

function badge(status: ServiceStatus | null, loading = false): string {
  if (loading) return `<span style="color:var(--text3);font-size:11px;font-family:var(--mono);">Testing…</span>`;
  if (!status)  return '';
  const col = status.ok ? 'var(--green)' : 'var(--red)';
  const icon = status.ok ? '✓' : '✗';
  return `<span style="color:${col};font-size:11px;font-family:var(--mono);">${icon} ${esc(status.msg)}</span>`;
}

function setStatus(id: string, status: ServiceStatus | null, loading = false): void {
  const el = document.getElementById(id);
  if (el) el.innerHTML = badge(status, loading);
}

// ── Helpers for reading inputs (keep existing value if field left blank) ───────

function inputVal(id: string, existingKey: string): string {
  const entered = (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? '';
  return entered || get(existingKey);
}

// ── Modal ─────────────────────────────────────────────────────────────────────

export function openSettings(): void {
  document.getElementById('settings-modal')?.remove();

  const inputStyle = 'width:100%;background:var(--bg3);border:1px solid var(--border);color:var(--text);font-family:var(--mono);font-size:12px;padding:8px 10px;border-radius:3px;outline:none;box-sizing:border-box;';
  const labelStyle = 'display:block;font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--text3);margin-bottom:5px;margin-top:12px;';
  const sectionStyle = 'border:1px solid var(--border);border-radius:4px;padding:16px;margin-bottom:14px;';
  const sectionTitleStyle = 'font-family:var(--mono);font-size:11px;font-weight:700;color:var(--text2);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:4px;display:flex;justify-content:space-between;align-items:center;';
  const testBtnStyle = 'background:rgba(0,212,255,0.08);border:1px solid rgba(0,212,255,0.2);color:var(--accent);font-family:var(--mono);font-size:10px;padding:3px 10px;cursor:pointer;border-radius:2px;letter-spacing:0.08em;';

  const modal = document.createElement('div');
  modal.id = 'settings-modal';
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.8);z-index:10000;display:flex;align-items:center;justify-content:center;padding:20px;overflow-y:auto;';

  const ph = (key: string, fallback: string) => masked(key) || fallback;

  modal.innerHTML = `
    <div style="background:var(--bg2);border:1px solid var(--border);border-radius:6px;padding:28px;width:100%;max-width:560px;max-height:90vh;overflow-y:auto;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
        <div style="font-family:var(--sans);font-size:18px;font-weight:800;color:var(--text);">⚙ Settings</div>
        <button id="settings-close" style="background:none;border:none;color:var(--text3);font-size:18px;cursor:pointer;line-height:1;">✕</button>
      </div>
      <div style="font-size:10px;color:var(--text3);margin-bottom:20px;font-family:var(--mono);">Leave a field blank to keep its existing value. Keys sync across devices via KV.</div>

      <!-- Alpaca -->
      <div style="${sectionStyle}">
        <div style="${sectionTitleStyle}">
          <span>📋 Alpaca Paper Trading</span>
          <button id="test-alpaca" style="${testBtnStyle}">TEST</button>
        </div>
        <div id="status-alpaca" style="min-height:16px;margin-bottom:8px;"></div>
        <label style="${labelStyle}">API Key ID</label>
        <input id="s-alpaca-key" type="password" value="" placeholder="${esc(ph(FIELDS.alpacaKey, 'PKTEST…'))}" autocomplete="off" style="${inputStyle}">
        <label style="${labelStyle}">Secret Key</label>
        <input id="s-alpaca-secret" type="password" value="" placeholder="${esc(ph(FIELDS.alpacaSecret, 'Your secret key…'))}" autocomplete="off" style="${inputStyle}">
        <div style="font-size:10px;color:var(--text3);margin-top:5px;line-height:1.5;">Required for live prices, news feed, and paper trading. Free at <a href="https://alpaca.markets" target="_blank" style="color:var(--accent);">alpaca.markets</a>.</div>
      </div>

      <!-- Access Key -->
      <div style="${sectionStyle}">
        <div style="${sectionTitleStyle}">
          <span>🔑 Access Key</span>
          <button id="test-auth" style="${testBtnStyle}">TEST</button>
        </div>
        <div id="status-auth" style="min-height:16px;margin-bottom:8px;"></div>
        <label style="${labelStyle}">Bearer Token</label>
        <input id="s-auth-token" type="password" value="" placeholder="${esc(ph(FIELDS.authToken, 'Your MCP_AUTH_TOKEN value…'))}" autocomplete="off" style="${inputStyle}">
        <div style="font-size:10px;color:var(--text3);margin-top:5px;line-height:1.5;">Protects Deep Research and Brief. Also used to sync settings across devices. Use your Worker's <code style="color:var(--accent2);background:var(--bg3);padding:1px 4px;border-radius:2px;">MCP_AUTH_TOKEN</code> secret.</div>
      </div>

      <!-- Quiver Quantitative -->
      <div style="${sectionStyle}">
        <div style="${sectionTitleStyle}">
          <span>📊 Quiver Quantitative</span>
          <button id="test-quiver" style="${testBtnStyle}">TEST</button>
        </div>
        <div id="status-quiver" style="min-height:16px;margin-bottom:8px;"></div>
        <label style="${labelStyle}">API Key</label>
        <input id="s-quiver-key" type="password" value="" placeholder="${esc(ph(FIELDS.quiverKey, 'Your Quiver API key…'))}" autocomplete="off" style="${inputStyle}">
        <div style="font-size:10px;color:var(--text3);margin-top:5px;line-height:1.5;">Required for Congressional Trading data. Free at <a href="https://quiverquant.com" target="_blank" style="color:var(--accent);">quiverquant.com</a>.</div>
      </div>

      <!-- Whale Alert -->
      <div style="${sectionStyle}">
        <div style="${sectionTitleStyle}">
          <span>🐋 Whale Alert</span>
          <button id="test-whale-alert" style="${testBtnStyle}">TEST</button>
        </div>
        <div id="status-whale-alert" style="min-height:16px;margin-bottom:8px;"></div>
        <label style="${labelStyle}">API Key</label>
        <input id="s-whale-alert-key" type="password" value="" placeholder="${esc(ph(FIELDS.whaleAlertKey, 'Your Whale Alert API key…'))}" autocomplete="off" style="${inputStyle}">
        <div style="font-size:10px;color:var(--text3);margin-top:5px;line-height:1.5;">Required for automated whale feed. Free at <a href="https://whale-alert.io" target="_blank" style="color:var(--accent);">whale-alert.io</a>.</div>
      </div>

      <!-- Etherscan -->
      <div style="${sectionStyle}">
        <div style="${sectionTitleStyle}">
          <span>⛓ Etherscan</span>
          <button id="test-etherscan" style="${testBtnStyle}">TEST</button>
        </div>
        <div id="status-etherscan" style="min-height:16px;margin-bottom:8px;"></div>
        <label style="${labelStyle}">API Key</label>
        <input id="s-etherscan-key" type="password" value="" placeholder="${esc(ph(FIELDS.etherscanKey, 'Your Etherscan API key…'))}" autocomplete="off" style="${inputStyle}">
        <div style="font-size:10px;color:var(--text3);margin-top:5px;line-height:1.5;">Required for Whale Tracker. Free at <a href="https://etherscan.io/apis" target="_blank" style="color:var(--accent);">etherscan.io/apis</a>.</div>
      </div>

      <!-- Google Sheets -->
      <div style="${sectionStyle}">
        <div style="${sectionTitleStyle}">
          <span>🗄 Google Sheets <span style="color:var(--text3);font-weight:400;">(optional)</span></span>
          <button id="test-sheets" style="${testBtnStyle}">TEST</button>
        </div>
        <div id="status-sheets" style="min-height:16px;margin-bottom:8px;"></div>
        <label style="${labelStyle}">Apps Script URL</label>
        <input id="s-script-url" type="text" value="${esc(get(FIELDS.scriptUrl))}" placeholder="https://script.google.com/macros/s/..." style="${inputStyle}">
        <label style="${labelStyle}">Token</label>
        <input id="s-script-token" type="password" value="" placeholder="${esc(ph(FIELDS.scriptToken, 'Your secret token…'))}" autocomplete="off" style="${inputStyle}">
        <div style="font-size:10px;color:var(--text3);margin-top:5px;line-height:1.5;">Required for adding/editing tickers, IPOs, roadmap items, and notes from the app.</div>
      </div>

      <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:4px;">
        <button id="settings-cancel" style="background:none;border:1px solid var(--border);color:var(--text2);font-family:var(--mono);font-size:11px;padding:8px 18px;cursor:pointer;border-radius:3px;">CANCEL</button>
        <button id="settings-save" style="background:rgba(0,212,255,0.1);border:1px solid rgba(0,212,255,0.4);color:var(--accent);font-family:var(--mono);font-size:11px;padding:8px 22px;cursor:pointer;border-radius:3px;font-weight:700;letter-spacing:0.08em;">SAVE</button>
      </div>
    </div>`;

  document.body.appendChild(modal);

  const close = () => modal.remove();
  modal.addEventListener('click', e => { if (e.target === modal) close(); });
  modal.querySelector('#settings-close')!.addEventListener('click', close);
  modal.querySelector('#settings-cancel')!.addEventListener('click', close);
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); } };
  document.addEventListener('keydown', onKey);

  // Save — only update a key if the user typed something; otherwise keep existing
  modal.querySelector('#settings-save')!.addEventListener('click', async () => {
    const newAlpacaKey    = inputVal('s-alpaca-key',    FIELDS.alpacaKey);
    const newAlpacaSecret = inputVal('s-alpaca-secret', FIELDS.alpacaSecret);
    const newAuthToken    = inputVal('s-auth-token',    FIELDS.authToken);
    const newQuiver       = inputVal('s-quiver-key',    FIELDS.quiverKey);
    const newEtherscan    = inputVal('s-etherscan-key', FIELDS.etherscanKey);
    const newWhale        = inputVal('s-whale-alert-key', FIELDS.whaleAlertKey);
    const newScriptUrl    = (document.getElementById('s-script-url')   as HTMLInputElement).value.trim() || get(FIELDS.scriptUrl);
    const newScriptToken  = inputVal('s-script-token',  FIELDS.scriptToken);

    set(FIELDS.alpacaKey,    newAlpacaKey);
    set(FIELDS.alpacaSecret, newAlpacaSecret);
    set(FIELDS.authToken,    newAuthToken);
    set(FIELDS.quiverKey,    newQuiver);
    set(FIELDS.etherscanKey, newEtherscan);
    set(FIELDS.whaleAlertKey, newWhale);
    set(FIELDS.scriptUrl,    newScriptUrl);
    set(FIELDS.scriptToken,  newScriptToken);

    close();
    await saveToKV();
  });

  // Test handlers — use entered value if present, otherwise fall back to stored value
  function getInputOrStored(inputId: string, storeKey: string): string {
    return (document.getElementById(inputId) as HTMLInputElement | null)?.value.trim() || get(storeKey);
  }

  modal.querySelector('#test-alpaca')!.addEventListener('click', async () => {
    const k = getInputOrStored('s-alpaca-key',    FIELDS.alpacaKey);
    const s = getInputOrStored('s-alpaca-secret', FIELDS.alpacaSecret);
    const prev = [get(FIELDS.alpacaKey), get(FIELDS.alpacaSecret)];
    set(FIELDS.alpacaKey, k); set(FIELDS.alpacaSecret, s);
    setStatus('status-alpaca', null, true);
    setStatus('status-alpaca', await testAlpaca());
    set(FIELDS.alpacaKey, prev[0]); set(FIELDS.alpacaSecret, prev[1]);
  });

  modal.querySelector('#test-auth')!.addEventListener('click', async () => {
    const tok = getInputOrStored('s-auth-token', FIELDS.authToken);
    const prev = get(FIELDS.authToken);
    set(FIELDS.authToken, tok);
    setStatus('status-auth', null, true);
    setStatus('status-auth', await testAuth());
    set(FIELDS.authToken, prev);
  });

  modal.querySelector('#test-quiver')!.addEventListener('click', async () => {
    const key = getInputOrStored('s-quiver-key', FIELDS.quiverKey);
    const prev = get(FIELDS.quiverKey);
    set(FIELDS.quiverKey, key);
    setStatus('status-quiver', null, true);
    setStatus('status-quiver', await testQuiver());
    set(FIELDS.quiverKey, prev);
  });

  modal.querySelector('#test-whale-alert')!.addEventListener('click', async () => {
    const key = getInputOrStored('s-whale-alert-key', FIELDS.whaleAlertKey);
    const prevKey = get(FIELDS.whaleAlertKey);
    set(FIELDS.whaleAlertKey, key);
    setStatus('status-whale-alert', null, true);
    try {
      const base = 'https://twilight-dew-3ae7.delfrost42.workers.dev';
      const r = await fetch(`${base}/api/whale-feed`, {
        headers: { 'X-Whale-Alert-Key': key, 'Authorization': `Bearer ${get(FIELDS.authToken)}` },
        signal: AbortSignal.timeout(10000),
      });
      const d = await r.json() as any;
      if (d.error) setStatus('status-whale-alert', { ok: false, msg: d.error });
      else setStatus('status-whale-alert', { ok: true, msg: `Connected · ${d.count ?? 0} recent transactions` });
    } catch(e: unknown) {
      setStatus('status-whale-alert', { ok: false, msg: e instanceof Error ? e.message : 'Request failed' });
    }
    set(FIELDS.whaleAlertKey, prevKey);
  });

  modal.querySelector('#test-etherscan')!.addEventListener('click', async () => {
    const key = getInputOrStored('s-etherscan-key', FIELDS.etherscanKey);
    const prevKey = get(FIELDS.etherscanKey);
    set(FIELDS.etherscanKey, key);
    setStatus('status-etherscan', null, true);
    try {
      const base = 'https://twilight-dew-3ae7.delfrost42.workers.dev';
      const r = await fetch(`${base}/api/wallet?address=0xd8dA6BF26964aF9D7eEd9e03E53415D37aA96045`, {
        headers: { 'X-Etherscan-Key': key, 'Authorization': `Bearer ${get(FIELDS.authToken)}` },
        signal: AbortSignal.timeout(10000),
      });
      const d = await r.json() as any;
      if (d.error) setStatus('status-etherscan', { ok: false, msg: d.error });
      else setStatus('status-etherscan', { ok: true, msg: `Connected · vitalik.eth: ${d.ethBalance} ETH` });
    } catch(e: unknown) {
      setStatus('status-etherscan', { ok: false, msg: e instanceof Error ? e.message : 'Request failed' });
    }
    set(FIELDS.etherscanKey, prevKey);
  });

  modal.querySelector('#test-sheets')!.addEventListener('click', async () => {
    setStatus('status-sheets', null, true);
    setStatus('status-sheets', await testSheets());
  });

  // Auto-run tests if already configured
  if (get(FIELDS.alpacaKey) && get(FIELDS.alpacaSecret)) testAlpaca().then(s => setStatus('status-alpaca', s));
  if (get(FIELDS.authToken)) testAuth().then(s => setStatus('status-auth', s));
  if (get(FIELDS.quiverKey)) testQuiver().then(s => setStatus('status-quiver', s));
}
